// components/StoryIdeaForm.tsx — collapsible "story idea / feedback" form.
//
// Renders at the bottom of the StoryPicker for every signed-in user (free,
// subscriber, locked). No subscription gate. Reachable only from the picker
// — never in-game.
//
// Behaviour:
//   - Collapsed: a small italic "Have a story idea or feedback?" row + [+]
//     glyph. Click expands.
//   - Expanded: helper copy + autofocused textarea (4-6 rows) + char counter
//     (turns coral past 4000) + Cancel + Send.
//   - Submit disabled when value.trim().length < 10 or > 4000.
//   - On success: collapse + clear + show inline "Thanks — got it." for ~3s.
//   - On IdeasRateLimitedError: keep open, show inline error
//     "You've sent 5 today — try tomorrow."
//   - On other errors: keep open, show the error message inline.
//
// All styles inline; doesn't touch web/src/styles.ts. Centred max-width 540px
// so on wide screens it doesn't sprawl across the picker card.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  submitStoryIdea,
  StoryIdeasApiError,
  IdeasRateLimitedError,
} from "../services/storyIdeasApi";

const MIN_LEN = 10;
const MAX_LEN = 4000;
const POST_SUBMIT_CONFIRM_MS = 3000;

export default function StoryIdeaForm() {
  const { t } = useTranslation(["game", "common"]);
  const [expanded, setExpanded] = useState(false);
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentlySent, setRecentlySent] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Autofocus the textarea on expand. Doesn't run on first render (expanded
  // is false at mount) and only retriggers if the user collapses + reopens.
  useEffect(() => {
    if (expanded && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [expanded]);

  // Auto-fade the post-submit confirmation row.
  useEffect(() => {
    if (!recentlySent) return;
    const t = window.setTimeout(() => setRecentlySent(false), POST_SUBMIT_CONFIRM_MS);
    return () => window.clearTimeout(t);
  }, [recentlySent]);

  const trimmedLen = value.trim().length;
  const isValid = trimmedLen >= MIN_LEN && trimmedLen <= MAX_LEN;
  const overCap = value.length > MAX_LEN;

  function handleExpand() {
    if (submitting) return;
    setExpanded(true);
    setError(null);
  }

  function handleCancel() {
    if (submitting) return;
    setExpanded(false);
    setValue("");
    setError(null);
  }

  async function handleSend() {
    if (submitting || !isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitStoryIdea(value.trim());
      // Success — collapse, clear, show inline confirm.
      setValue("");
      setExpanded(false);
      setRecentlySent(true);
    } catch (err) {
      if (err instanceof IdeasRateLimitedError) {
        setError(t("game:storyIdeas.rateLimited"));
      } else if (err instanceof StoryIdeasApiError) {
        setError(err.message || t("common:errors.couldntSend"));
      } else if (err instanceof Error) {
        setError(err.message || t("common:errors.couldntSend"));
      } else {
        setError(t("common:errors.couldntSend"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={wrapperStyle}>
      <div style={dividerStyle} aria-hidden="true" />

      {!expanded ? (
        <button
          type="button"
          onClick={handleExpand}
          style={collapsedRowStyle}
          aria-expanded={false}
          aria-label={t("game:storyIdeas.collapsedLabel")}
        >
          <span style={collapsedLabelStyle}>{t("game:storyIdeas.collapsedLabel")}</span>
          <span style={collapsedPlusStyle} aria-hidden="true">+</span>
        </button>
      ) : (
        <div style={expandedShellStyle}>
          <p style={helperStyle}>{t("game:storyIdeas.expandedHelper")}</p>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              // Cheap soft-cap at MAX_LEN * 1.1 — prevent users from pasting
              // megabytes; visual feedback (counter going coral) handles the
              // exact 4000 boundary. Server-side bound is the source of truth.
              const next = e.target.value.slice(0, Math.floor(MAX_LEN * 1.1));
              setValue(next);
              if (error) setError(null);
            }}
            disabled={submitting}
            rows={5}
            placeholder={t("game:storyIdeas.placeholder")}
            style={textareaStyle}
            aria-label={t("game:storyIdeas.ariaLabel")}
            maxLength={Math.floor(MAX_LEN * 1.1)}
          />
          <div style={counterRowStyle}>
            <span
              style={{
                ...counterStyle,
                ...(overCap ? counterErrorStyle : {}),
              }}
            >
              {value.length} / {MAX_LEN}
            </span>
            {trimmedLen > 0 && trimmedLen < MIN_LEN && (
              <span style={hintStyle}>
                {t("game:storyIdeas.atLeastChars", { count: MIN_LEN })}
              </span>
            )}
          </div>
          {error && <div style={errorStyle} role="alert">{error}</div>}
          <div style={actionsRowStyle}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                ...cancelBtnStyle,
                ...(submitting ? disabledStyle : {}),
              }}
            >
              {t("common:buttons.cancel")}
            </button>
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!isValid || submitting}
              style={{
                ...sendBtnStyle,
                ...(!isValid || submitting ? disabledStyle : {}),
              }}
            >
              {submitting
                ? t("common:buttons.sending")
                : t("game:storyIdeas.sendCta")}
            </button>
          </div>
        </div>
      )}

      {recentlySent && (
        <div style={confirmStyle} role="status" aria-live="polite">
          {t("game:storyIdeas.thanks")}
        </div>
      )}
    </div>
  );
}

// ── Styles (inline — see header) ────────────────────────────────────────

const wrapperStyle: CSSProperties = {
  marginTop: 28,
  width: "100%",
  maxWidth: 540,
  marginLeft: "auto",
  marginRight: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 8,
};

const dividerStyle: CSSProperties = {
  height: 1,
  background: "rgba(212,165,116,0.18)",
  marginBottom: 14,
};

const collapsedRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  width: "100%",
  background: "transparent",
  border: "none",
  padding: "6px 4px",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
};

const collapsedLabelStyle: CSSProperties = {
  fontStyle: "italic",
  color: "#7a6243",
  fontSize: "0.95rem",
  letterSpacing: "0.02em",
};

const collapsedPlusStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "1rem",
  lineHeight: 1,
};

const expandedShellStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: "12px 4px 4px",
};

const helperStyle: CSSProperties = {
  margin: 0,
  color: "#a89070",
  fontStyle: "italic",
  fontSize: "0.9rem",
  lineHeight: 1.45,
};

const textareaStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(28,20,14,0.75)",
  border: "1px solid rgba(212,165,116,0.35)",
  color: "#e8d9b8",
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  fontSize: "1rem",
  lineHeight: 1.5,
  padding: "10px 12px",
  outline: "none",
  borderRadius: 2,
  resize: "vertical",
  minHeight: 110,
};

const counterRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const counterStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
};

const counterErrorStyle: CSSProperties = {
  color: "#c47a5a",
};

const hintStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.72rem",
  fontStyle: "italic",
};

const errorStyle: CSSProperties = {
  color: "#c47a5a",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.78rem",
  letterSpacing: "0.04em",
  padding: "6px 8px",
  border: "1px solid rgba(196,122,90,0.4)",
  background: "rgba(196,122,90,0.08)",
  borderRadius: 2,
};

const actionsRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 10,
  marginTop: 4,
};

const cancelBtnStyle: CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(212,165,116,0.28)",
  color: "#a89070",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.78rem",
  letterSpacing: "0.06em",
  padding: "6px 14px",
  cursor: "pointer",
  borderRadius: 2,
};

const sendBtnStyle: CSSProperties = {
  background: "rgba(212,165,116,0.18)",
  border: "1px solid rgba(212,165,116,0.5)",
  color: "#e8c896",
  fontFamily: "'IM Fell English', Georgia, serif",
  fontSize: "0.9rem",
  letterSpacing: "0.04em",
  padding: "6px 16px",
  cursor: "pointer",
  borderRadius: 2,
};

const disabledStyle: CSSProperties = {
  opacity: 0.5,
  cursor: "not-allowed",
};

const confirmStyle: CSSProperties = {
  alignSelf: "center",
  marginTop: 6,
  padding: "4px 12px",
  color: "#9ad6b1",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.78rem",
  letterSpacing: "0.04em",
  fontStyle: "italic",
};
